<script setup lang="ts">
import { useSiteStore } from '~/stores/site.store';
import { useAuthStore } from '~/stores/auth.store';
import { AccountModalsStrings } from '~/interfaces/dto/general/modals';
import { trackingMethods } from '~/plugins/analytics.client';
import Button from '../design-system/button.vue';
import { globalKeys } from 'assets/data/global-keys';

interface IBannerProps {
  vertical?: string;
  desktopAspectRatio?: string;
  mobileAspectRatio?: string;
  desktopPlaceholder?: string;
  mobilePlaceholder?: string;
  autoplay?: number;
}

const viewport = useViewport();
const runtimeConfig = useRuntimeConfig();
const authStore = useAuthStore();
const siteStore = useSiteStore();
const locale = useLocaleStore();
const router = useRouter();

const { getPlaceholder } = useBranding();

useScript({
  src: runtimeConfig.public.bannerWidgetUrl,
  type: 'module',
  tagPosition: 'head',
  async: 'true',
  defer: 'true',
  id: 'banner-widget',
});

const activeModal = ref<boolean>(false);
const modalContent = ref<string>('');
const noBanners = ref<boolean>(false);
const props = withDefaults(defineProps<IBannerProps>(), {
  vertical: '',
  desktopAspectRatio: '16/3',
  mobileAspectRatio: '36/11',
  displayPlaceHolder: false,
  autoplay: 0,
});

const handleBannerClick = (action: { Action: string; Value: string }) => {
  switch (action.Action) {
    case 'NavigateTo':
    case 'LaunchGame':
      router.push(action.Value.toLowerCase());
      break;
    case 'Launch':
      const detail =
        action.Value === 'Registration' ? 'register' : action.Value;
      AccountModalsStrings.includes(detail.toLowerCase())
        ? authStore.setAccountModalPage(detail.toLowerCase())
        : siteStore.activateModal(detail.toLowerCase());
      break;
  }
  trackingMethods.bannerClicked();
};

const handleMoreInfoClick = (content: string) => {
  modalContent.value = content;
  activeModal.value = true;
  trackingMethods.bannerInfoClicked();
};

const playerIdCompute = computed(() =>
  !!authStore.user_id ? authStore.user_id : '',
);

const bannerPlaceholders = computed(() => {
  return {
    mobile: !!props.mobilePlaceholder
      ? props.mobilePlaceholder
      : getPlaceholder('bannerPlaceholderMobile'),
    desktop: !!props.desktopPlaceholder
      ? props.desktopPlaceholder
      : getPlaceholder('bannerPlaceholderDesktop'),
  };
});

const memoFlag = computed(() => {
  const defaultKey = [
    props.vertical,
    playerIdCompute.value,
    props.mobileAspectRatio,
    props.desktopAspectRatio,
  ];

  return import.meta.dev
    ? [
        locale.getLocale.split('-')[0],
        siteStore.getRegionCode,
        props.autoplay,
        ...defaultKey,
      ]
    : defaultKey;
});
</script>

<template>
  <div>
    <div v-if="!noBanners" class="md:px-0 text-dark-900 dark:text-white">
      <LazyDesignSystemModal
        :title="$t('more-info')"
        :active="activeModal"
        content-type="form"
        @close-modal="[(activeModal = false), (modalContent = '')]"
      >
        <template #modal-content>
          <div class="p-4 dark:text-white" v-html="modalContent" />
        </template>
        <template #footer-content>
          <div class="p-2">
            <Button
              element-name="banner-okay"
              class="w-full capitalize flex-center"
              @click="[(activeModal = false), (modalContent = '')]"
            >
              {{ $t('okay') }}
            </Button>
          </div>
        </template>
      </LazyDesignSystemModal>
      <div
        class="mx-auto pt-2 md:pt-3 md:pb-2 xs:pt-0 xs:pb-0 relative rounded-md"
      >
        <div
          class="rounded-md relative overflow-hidden container"
          :style="{
            aspectRatio: `${
              viewport.isLessThan('tablet')
                ? `${props.mobileAspectRatio}`
                : `${props.desktopAspectRatio}`
            }`,
          }"
        >
          <Suspense>
            <cta-banner
              id="jpc-banner"
              :v-memo="memoFlag"
              channel-code="webDesktop"
              display-placeholder="true"
              storage-type="cookie"
              :key-name="globalKeys.authKeys.accessToken"
              license="jackpotcityafrica"
              :locale="locale.getLocale.split('-')[0]"
              :vertical="props.vertical"
              :region="siteStore.getRegionCode"
              :player-id="playerIdCompute"
              :style="{
                aspectRatio: `${
                  viewport.isLessThan('tablet')
                    ? `${props.mobileAspectRatio}`
                    : `${props.desktopAspectRatio}`
                }`,
              }"
              :config.prop="{
                placeholders: {
                  ...bannerPlaceholders,
                },
                aspectRatios: {
                  mobile: props.mobileAspectRatio,
                  desktop: props.desktopAspectRatio,
                },
                autoplay: props.autoplay,
              }"
              @bannerClick="($event) => handleBannerClick($event.detail)"
              @noBanners="noBanners = $event"
              @infoClick="handleMoreInfoClick($event.detail)"
            >
              <UserInterfaceGenericSkeleton
                #placeholder
                :style="{
                  aspectRatio: `${
                    viewport.isLessThan('tablet')
                      ? `${props.mobileAspectRatio}`
                      : `${props.desktopAspectRatio}`
                  }`,
                }"
              />
            </cta-banner>
          </Suspense>
        </div>
      </div>
    </div>
  </div>
</template>
